import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { livelloModulo } from '../../auth/permessi'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable } from '../DataTable'

/**
 * CENSO MED: the device identities read from the DICOM and HL7 on the wire —
 * only device fields (manufacturer, model, serial, UID, software), never
 * patient data. Off by default; the hospital turns it on here (opt-in), with
 * the CENSO MED licence. See `med_wire_identity_service`.
 */
export function VistaIdentitaFilo() {
  const { principal } = useAuth()
  const scrive = livelloModulo(principal, 'medical') === 'write'
  const vedeCmdb = livelloModulo(principal, 'cis') !== 'hidden'
  const dati = useAsync(() => api.medWireIdentities(), [])
  const d = dati.data

  async function cambia(acceso: boolean) {
    await api.medSettings({ wire_identity: acceso })
    await dati.reload()
  }

  if (dati.loading && !d) return <Spinner />
  if (!d) return <ErrorBanner error={dati.error} />

  return (
    <>
      <p className="field__hint">
        {t('CENSO legge dai messaggi DICOM e HL7 che vede sul filo la sola identità degli apparecchi — costruttore, modello, numero di serie, Device UID/UDI, versione software, modalità, stazione, struttura e reparto, date di fabbricazione, installazione e calibrazione. Non legge mai dati del paziente: in DICOM salta il gruppo del paziente e si ferma prima dei pixel; in HL7 legge solo i segmenti dell’apparecchiatura, mai le misure cliniche. I dispositivi sono distinti per numero di serie o UDI, mai per solo indirizzo. «Letture» conta quante volte l’apparecchio si è fatto riconoscere sul filo, per una statistica d’uso; quando il numero di serie o l’UDI combacia con il suo indirizzo, l’identità arricchisce il dispositivo nel CMDB (modello e seriale esatti). Gli apparecchi visti solo attraverso un PACS/archivio (un unico indirizzo che riceve immagini da molte modalità, anche di altre strutture) sono segnati «solo in archivio» e non diventano dispositivi: sono studi conservati, non apparecchi locali.')}
      </p>
      <div className="checkbox">
        <input id="wire-identity" type="checkbox" checked={d.enabled} disabled={!scrive}
               onChange={(e) => void cambia(e.target.checked)} />
        <label htmlFor="wire-identity">
          {t('Leggi l’identità degli apparecchi da DICOM e HL7 sul filo')}
          <small>
            {t('Da attivare dopo averlo concordato e documentato con l’ospedale (DPIA). Richiede la licenza CENSO MED e che la sonda veda il traffico (porta mirror). Serve l’aggiornamento delle sonde.')}
          </small>
        </label>
      </div>
      <Card title={t('Apparecchi riconosciuti dal filo')}>
        {d.identities.length === 0 ? (
          <EmptyState title={d.enabled
            ? t('Ancora nessuna identità letta: serve che la sonda veda il traffico DICOM/HL7 degli apparecchi.')
            : t('Lettura spenta. Attivala qui sopra per riconoscere gli apparecchi dai loro messaggi.')} />
        ) : (
          <DataTable
            id="medical-wire-identity"
            rows={d.identities}
            rowKey={(r) => r.id}
            initialSort={{ key: 'last_seen', dir: 'desc' }}
            conteggio={{ nome: t('apparecchi') }}
            nomeEsportazione="elettromedicali-identita-filo"
            columns={[
              { key: 'manufacturer', header: t('Costruttore'), value: (r) => r.manufacturer },
              { key: 'model', header: t('Modello'), value: (r) => r.model },
              { key: 'serial', header: t('Numero di serie'), mono: true, value: (r) => r.serial },
              { key: 'device_uid', header: 'Device UID / UDI', mono: true, value: (r) => r.device_uid, hidden: true },
              { key: 'device_type', header: t('Tipo'), filter: 'select', value: (r) => r.device_type },
              { key: 'software', header: t('Versione software'), mono: true, value: (r) => r.software },
              { key: 'source', header: t('Protocollo'), filter: 'select', value: (r) => r.source.toUpperCase() },
              { key: 'station', header: t('Stazione'), value: (r) => r.station, hidden: true },
              { key: 'department', header: t('Reparto'), value: (r) => r.department, hidden: true },
              { key: 'institution', header: t('Struttura'), value: (r) => r.institution, hidden: true },
              { key: 'installed', header: t('Installazione'), value: (r) => r.installed, hidden: true },
              { key: 'calibrated', header: t('Ultima calibrazione'), value: (r) => r.calibrated, hidden: true },
              { key: 'observations', header: t('Letture'), type: 'number', align: 'right', value: (r) => r.observations },
              { key: 'last_active', header: t('Ultima acquisizione'), value: (r) => r.last_active ?? '' },
              { key: 'addresses', header: t('Indirizzi visti'), mono: true, value: (r) => r.addresses.join(', ') },
              { key: 'ci', header: t('Dispositivo'),
                value: (r) => (r.device_id ? t('apri la scheda') : r.archived ? t('solo in archivio') : r.ci_id ? t('abbinato') : t('non ancora')),
                render: (r) => r.device_id
                  ? <>{r.archived && <span className="badge badge--muted" style={{ marginRight: 6 }} title={t('Dispositivo esterno: identificato solo attraverso un PACS/archivio.')}>{t('esterno')}</span>}<Link to={`/medical/devices/${r.device_id}`}>{t('apri la scheda')}</Link></>
                  : r.archived
                    ? <span className="text-muted" title={t('Visto solo attraverso un PACS/archivio: è uno studio archiviato o inoltrato, spesso di un’altra struttura, non un apparecchio locale.')}>{t('solo in archivio')}</span>
                    : r.ci_id
                      ? (vedeCmdb ? <Link to={`/cis/${r.ci_id}`}>{t('abbinato (CI)')}</Link> : <span>{t('abbinato')}</span>)
                      : <span className="text-muted">{t('non ancora')}</span> },
              { key: 'last_seen', header: t('Ultima volta'), type: 'date', value: (r) => r.last_seen ?? '', render: (r) => formatDateTime(r.last_seen) },
            ]}
          />
        )}
      </Card>
    </>
  )
}
